import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@store/store';
import { useLazyGetUserPlansQuery } from '@api/usePlans';
import {
  PlanError,
  setCurrentPlanFeatures,
  setFreePlanFeatures,
  setLitePlanFeatures,
  setPlanFeatureFlag
} from '@store/AppSlice';
import { PricingPlans } from '@constants/pricing';
import { FeatureTypes } from '@constants/common';

interface PlanLimits {
  SIZE_LIMIT: number;
  PROJECT_LIMIT: number;
  PROJECT_TRIAL_LIMIT: number;
  FILE_FORMATS: string[];
  PAID_FEATURES: string[];
  ROW_LIMIT: number;
  COLUMNS_LIMIT: number;
  SQB_LIMIT_MONTHLY: number;
  MAX_MONTHLY_DATA_UPDATE_LIMIT: number;
  MAX_MONTHLY_RECALCULATION_LIMIT: number;
  MAX_MONTHLY_INSIGHTS_LIMIT: number;
  PROJECT_LIMIT_MONTHLY: number;
  MAX_MONTHLY_API_POST_REQUESTS: number;
}
const usePricing = () => {
  const planLimitsFlag = `${process.env.REACT_APP_PLAN_FLAG}` === 'TRUE';
  const dispatch = useDispatch();
  const errorData = useSelector(
    (state: RootState) => state?.AppData?.planUpdateErrors
  );
  const userId = useSelector(
    (state: RootState) => state?.userDetails?.user?.id
  );

  const currentUserPlan = useSelector(
    (state: RootState) => state?.userDetails?.plan
  );

  const subscriptionFlag = useSelector(
    (state: RootState) => state?.AppData?.subKeyFlag
  );

  const isTrialPlanUser = useSelector(
    (state: RootState) => state?.userDetails?.is_trial
  );

  const [planUpdateError, setPlanUpdateError] = useState<PlanError | null>(
    null
  );

  const [allPlans, setAllPlans] = useState<any>(null);

  const [getUserPlans] = useLazyGetUserPlansQuery();

  useEffect(() => {
    const getPlans = async () => {
      const response = await getUserPlans({});

      if (response.data) setAllPlans(response.data);
    };

    if (userId && planLimitsFlag) getPlans();
  }, [userId, planLimitsFlag]);

  // Hook to update plan features
  useEffect(() => {
    if (allPlans) {
      dispatch(
        setFreePlanFeatures(
          allPlans?.plan_details[PricingPlans.NO_ACTIVE_SUBSCRIPTION]
        )
      );
      dispatch(setLitePlanFeatures(allPlans?.plan_details[PricingPlans.LITE]));
    }
    if (allPlans && allPlans?.paid_plan_check) {
      const isLiteUser = currentUserPlan === PricingPlans.LITE;
      const freeFeaturesEligiblity =
        isTrialPlanUser ||
        currentUserPlan === PricingPlans.NO_ACTIVE_SUBSCRIPTION;

      const planFeatures: PlanLimits = allPlans.plan_details?.[currentUserPlan];

      dispatch(setPlanFeatureFlag(true));
      dispatch(
        setCurrentPlanFeatures({
          downloadCleanedFile:
            planFeatures?.PAID_FEATURES?.includes(
              FeatureTypes.DOWNLOAD_CLEANED_FILE
            ) || false,
          downloadReport:
            planFeatures?.PAID_FEATURES?.includes(
              FeatureTypes.DOWNLOAD_REPORTS
            ) || false,
          combineAppendFile:
            planFeatures?.PAID_FEATURES?.includes(
              FeatureTypes.COMBINE_APPEND_FILE
            ) || false,
          autoCategorization:
            planFeatures?.PAID_FEATURES?.includes(
              FeatureTypes.AUTO_CATEGORIZATION
            ) || false,
          removeWaterMark:
            planFeatures?.PAID_FEATURES?.includes(
              FeatureTypes.REMOVE_WATERMARK
            ) || false,
          chartInsights:
            planFeatures?.PAID_FEATURES?.includes(
              FeatureTypes.CHART_INSIGHTS
            ) || false,
          projectInsights:
            planFeatures?.PAID_FEATURES?.includes(
              FeatureTypes.PROJECT_INSIGHTS
            ) || false,
          restApi:
            planFeatures?.PAID_FEATURES?.includes(FeatureTypes.REST_API) ||
            false,
          squirby:
            planFeatures?.PAID_FEATURES?.includes(FeatureTypes.SQUIRBY) ||
            false,
          manualUpdate:
            planFeatures?.PAID_FEATURES?.includes(FeatureTypes.MANUAL_UPDATE) ||
            false,
          autoAi:
            planFeatures?.PAID_FEATURES?.includes(FeatureTypes.AUTO_AI) ||
            false,
          sizeLimit: freeFeaturesEligiblity
            ? planFeatures?.SIZE_LIMIT || null
            : null,
          fileFormats: freeFeaturesEligiblity
            ? planFeatures?.FILE_FORMATS || []
            : [],
          projectLimit: isLiteUser
            ? planFeatures?.PROJECT_LIMIT_MONTHLY || 0
            : planFeatures?.PROJECT_LIMIT || 0,
          squirbyLimit: planFeatures?.SQB_LIMIT_MONTHLY || 0,
          insightsLimit: planFeatures?.MAX_MONTHLY_INSIGHTS_LIMIT || 0,
          gSheetUpdateLimit: planFeatures?.MAX_MONTHLY_DATA_UPDATE_LIMIT || 0,
          apiUpdateLimit: planFeatures?.MAX_MONTHLY_RECALCULATION_LIMIT || 0,
          apiUploadLimit: planFeatures?.MAX_MONTHLY_API_POST_REQUESTS || 0
        })
      );
    } else {
      dispatch(setPlanFeatureFlag(false));
    }
  }, [allPlans, currentUserPlan]);

  // Hoook to update plan errors
  useEffect(() => {
    if (subscriptionFlag) setPlanUpdateError(errorData);
  }, [subscriptionFlag, errorData]);

  return { planUpdateError };
};

export default usePricing;
